import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, getAuthHeader, handleApiError } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Card, CardContent } from '../components/ui/card';
import { Plus, Edit2, Trash2, Building2, MapPin, Phone } from 'lucide-react';

function FiliaisPage() {
  const [filiais, setFiliais] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [selectedFilial, setSelectedFilial] = React.useState(null);
  const [formData, setFormData] = React.useState({ nome: '', codigo: '', endereco: '', telefone: '' });

  React.useEffect(function() { fetchFiliais(); }, []);

  function fetchFiliais() {
    setLoading(true);
    axios.get(API + '/filiais', { headers: getAuthHeader() })
      .then(function(response) { setFiliais(response.data); })
      .catch(function(error) { handleApiError(error); toast.error('Erro ao carregar filiais'); })
      .finally(function() { setLoading(false); });
  }

  function openCreateModal() {
    setSelectedFilial(null);
    setFormData({ nome: '', codigo: '', endereco: '', telefone: '' });
    setModalOpen(true);
  }

  function openEditModal(filial) {
    setSelectedFilial(filial);
    setFormData({ nome: filial.nome, codigo: filial.codigo, endereco: filial.endereco || '', telefone: filial.telefone || '' });
    setModalOpen(true);
  }

  function handleSubmit(e) {
    e.preventDefault();
    var request = selectedFilial
      ? axios.put(API + '/filiais/' + selectedFilial.id, formData, { headers: getAuthHeader() })
      : axios.post(API + '/filiais', formData, { headers: getAuthHeader() });
    request.then(function() {
      toast.success(selectedFilial ? 'Filial atualizada!' : 'Filial criada!');
      setModalOpen(false);
      fetchFiliais();
    }).catch(function() { toast.error('Erro ao salvar'); });
  }

  function handleDelete() {
    axios.delete(API + '/filiais/' + selectedFilial.id, { headers: getAuthHeader() })
      .then(function() { toast.success('Filial excluída!'); setDeleteModalOpen(false); fetchFiliais(); })
      .catch(function() { toast.error('Erro ao excluir'); });
  }

  function updateForm(field, value) {
    setFormData(function(prev) { var newData = Object.assign({}, prev); newData[field] = value; return newData; });
  }

  return (
    <div className="space-y-6" data-testid="filiais-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">FILIAIS</h1>
          <p className="text-slate-500">{filiais.length} filiais cadastradas</p>
        </div>
        <Button data-testid="add-filial-btn" onClick={openCreateModal} className="bg-h4p-blue hover:bg-h4p-blue-hover text-white">
          <Plus className="w-4 h-4 mr-2" />Nova Filial
        </Button>
      </div>

      <Card className="bg-white border-slate-200 shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-800 border-slate-700 hover:bg-slate-800">
              <TableHead className="text-white font-bold text-sm uppercase tracking-wide py-4">Código</TableHead>
              <TableHead className="text-white font-bold text-sm uppercase tracking-wide py-4">Nome</TableHead>
              <TableHead className="text-white font-bold text-sm uppercase tracking-wide py-4">Endereço</TableHead>
              <TableHead className="text-white font-bold text-sm uppercase tracking-wide py-4">Telefone</TableHead>
              <TableHead className="text-white font-bold text-sm uppercase tracking-wide py-4 text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500 mx-auto"></div></TableCell></TableRow>
            ) : filiais.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-400">Nenhuma filial cadastrada</TableCell></TableRow>
            ) : filiais.map(function(filial, index) {
              return (
                <TableRow key={filial.id} className={`border-slate-200 hover:bg-slate-100 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                  <TableCell>
                    <span className="bg-violet-100 text-violet-600 px-3 py-1 rounded-full font-semibold text-sm">{filial.codigo}</span>
                  </TableCell>
                  <TableCell className="text-slate-800 font-medium">
                    <span className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-slate-400" />
                      {filial.nome}
                    </span>
                  </TableCell>
                  <TableCell className="text-slate-600">
                    {filial.endereco ? (
                      <span className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        {filial.endereco}
                      </span>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="text-slate-600">
                    {filial.telefone ? (
                      <span className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-slate-400" />
                        {filial.telefone}
                      </span>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button onClick={function() { openEditModal(filial); }} variant="ghost" size="sm" className="text-slate-400 hover:text-sky-500 hover:bg-sky-50">
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button onClick={function() { setSelectedFilial(filial); setDeleteModalOpen(true); }} variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader><DialogTitle className="text-slate-800">{selectedFilial ? 'Editar' : 'Nova'} Filial</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-600">Código *</Label>
                <Input value={formData.codigo} onChange={function(e) { updateForm('codigo', e.target.value.toUpperCase()); }} className="bg-white border-slate-300 text-slate-700" placeholder="Ex: SP, P4" required />
              </div>
              <div>
                <Label className="text-slate-600">Nome *</Label>
                <Input value={formData.nome} onChange={function(e) { updateForm('nome', e.target.value); }} className="bg-white border-slate-300 text-slate-700" placeholder="Nome da filial" required />
              </div>
            </div>
            <div>
              <Label className="text-slate-600">Endereço</Label>
              <Input value={formData.endereco} onChange={function(e) { updateForm('endereco', e.target.value); }} className="bg-white border-slate-300 text-slate-700" placeholder="Endereço completo" />
            </div>
            <div>
              <Label className="text-slate-600">Telefone</Label>
              <Input value={formData.telefone} onChange={function(e) { updateForm('telefone', e.target.value); }} className="bg-white border-slate-300 text-slate-700" placeholder="(00) 00000-0000" />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <Button type="button" onClick={function() { setModalOpen(false); }} variant="outline" className="bg-white border-slate-300 text-slate-600 hover:bg-slate-50">Cancelar</Button>
              <Button type="submit" className="bg-h4p-blue hover:bg-h4p-blue-hover text-white">Salvar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader><DialogTitle className="text-slate-800">Confirmar Exclusão</DialogTitle></DialogHeader>
          <p className="text-slate-600 py-4">Excluir a filial {selectedFilial ? selectedFilial.nome : ''}?</p>
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button onClick={function() { setDeleteModalOpen(false); }} variant="outline" className="bg-white border-slate-300 text-slate-600 hover:bg-slate-50">Cancelar</Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">Excluir</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default FiliaisPage;
